import * as React from "react";
import { Tabs } from "@/components/ui/tabs";
import { cn } from "@/components/utils/utils";
import { Badge } from "@/components/ui/badge";

// Varianti mobile per i tabs header
const MobileTabsList = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex w-full h-14 bg-white border-b border-gray-200 p-0 justify-around md:hidden",
      className
    )}
    {...props}
  />
));
MobileTabsList.displayName = "MobileTabsList";

const MobileTabsTrigger = React.forwardRef<
  React.ElementRef<"button">,
  React.ComponentPropsWithoutRef<"button"> & {
    active?: boolean;
    disabled?: boolean;
  }
>(({ className, active, disabled, children, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      "flex flex-col items-center justify-center gap-1 py-2 px-1 border-b-2 font-medium text-sm transition-colors relative flex-1 min-w-0",
      active
        ? "border-black text-black"
        : "border-transparent text-gray-500 hover:text-gray-700",
      disabled && "text-gray-400 cursor-not-allowed",
      className
    )}
    disabled={disabled}
    {...props}
  >
    {children}
  </button>
));
MobileTabsTrigger.displayName = "MobileTabsTrigger";

// Varianti desktop per i tabs header
const DesktopTabsList = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex w-full h-12 bg-transparent border-0 p-0 justify-start gap-4 md:gap-6 overflow-x-auto scrollbar-hide",
      className
    )}
    {...props}
  />
));
DesktopTabsList.displayName = "DesktopTabsList";

const DesktopTabsTrigger = React.forwardRef<
  React.ElementRef<"button">,
  React.ComponentPropsWithoutRef<"button"> & {
    active?: boolean;
    disabled?: boolean;
  }
>(({ className, active, disabled, children, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      "flex items-center gap-1 md:gap-2 py-3 px-2 md:px-1 border-b-2 font-medium md:transition-colors relative whitespace-nowrap flex-shrink-0",
      active
        ? "border-black text-black"
        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
      disabled && "text-gray-400 cursor-not-allowed",
      className
    )}
    disabled={disabled}
    {...props}
  >
    {children}
  </button>
));
DesktopTabsTrigger.displayName = "DesktopTabsTrigger";

// Componente riusabile per i tabs header
interface TabsHeaderProps {
  activeTab: string;
  onTabChange: (value: string) => void;
  tabs: Array<{
    value: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    disabled?: boolean;
    badge?: string;
    badgeVariant?: "default" | "secondary";
  }>;
  className?: string;
  sidebarCollapsed?: boolean;
  mobileTopOffset?: string;
  desktopTopOffset?: string;
}

const TabsHeader = React.forwardRef<HTMLDivElement, TabsHeaderProps>(
  ({ 
    activeTab, 
    onTabChange, 
    tabs, 
    className, 
    sidebarCollapsed = false,
    mobileTopOffset = "top-20",
    desktopTopOffset = "top-[80px]"
  }, ref) => {
    return (
      <div ref={ref} className={className}>
        {/* Mobile Tab Header */}
        <div className="md:hidden">
          <div className={cn(
            "fixed z-50 bg-white border-b border-gray-200 shadow-sm left-0 right-0",
            mobileTopOffset
          )}>
            <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
              <MobileTabsList>
                {tabs.map((tab) => (
                  <MobileTabsTrigger 
                    key={tab.value}
                    active={activeTab === tab.value}
                    disabled={tab.disabled}
                    onClick={() => onTabChange(tab.value)}
                  >
                    <div className="flex items-center gap-1">
                      <tab.icon className="h-4 w-4" />
                      <span className="text-sm">{tab.label}</span>
                      {tab.badge && (
                        <Badge 
                          variant={tab.badgeVariant === "secondary" ? "secondary" : "default"}
                          className="text-[10px] px-1 py-0 h-auto"
                        >
                          {tab.badge}
                        </Badge>
                      )}
                    </div>
                  </MobileTabsTrigger>
                ))}
              </MobileTabsList>
            </Tabs>
          </div>
          {/* Spacer for mobile */}
          <div className="h-14"></div>
        </div>

        {/* Desktop Tab Header */}
        <div className="hidden md:block">
          <div className={cn(
            "fixed z-50 bg-white border-b border-gray-200 shadow-sm transition-all duration-300 ease-in-out",
            desktopTopOffset,
            sidebarCollapsed ? "md:left-[70px]" : "md:left-[222px]"
          )} style={{ right: 0 }}>
            <div className="mx-auto px-6">
              <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
                <DesktopTabsList>
                  {tabs.map((tab) => (
                    <DesktopTabsTrigger 
                      key={tab.value}
                      active={activeTab === tab.value}
                      disabled={tab.disabled}
                      onClick={() => onTabChange(tab.value)}
                    >
                      <tab.icon className="h-4 w-4" />
                      {tab.label}
                      {tab.badge && (
                        <Badge 
                          variant={tab.badgeVariant === "secondary" ? "secondary" : "default"}
                          className="text-sm px-1.5 py-0.5 ml-1 h-auto"
                        >
                          {tab.badge}
                        </Badge>
                      )}
                    </DesktopTabsTrigger>
                  ))}
                </DesktopTabsList>
              </Tabs>
            </div>
          </div>
          {/* Spacer for desktop */}
          <div className="h-6"></div>
        </div>
      </div>
    );
  }
);
TabsHeader.displayName = "TabsHeader";

export { 
  TabsHeader,
  MobileTabsList,
  MobileTabsTrigger,
  DesktopTabsList,
  DesktopTabsTrigger
};
